/**
 * @workspace/strings — the resolver.
 *
 * Dependency-free i18n core. Runs identically in Node (esbuild), the browser
 * (Vite), React Native (Hermes/Metro), and Electron main. There is no i18n
 * framework here: catalogs are plain `as const` objects ("just the texts") and
 * this file is the entire ~runtime.
 *
 * Two leaf shapes:
 *   - a plain string:        "Save"
 *   - a plural form object:  { one: "{count} device", other: "{count} devices" }
 *
 * Interpolation uses single-brace `{name}` tokens. Unknown tokens pass through
 * unchanged so a missing param is visible rather than silently dropped.
 *
 * Keys are a flat dotted union (`namespace.section.item`) derived from the
 * surface's catalog shape, so a typo or removed key fails `tsc` — that is the
 * primary safety net for the extraction.
 */

export type PluralForm = { readonly one: string; readonly other: string };
export type Leaf = string | PluralForm;

/** A catalog is an arbitrarily-nested tree whose leaves are strings or plural forms. */
export interface Catalog {
  readonly [key: string]: Leaf | Catalog;
}

export type Params = Record<string, string | number>;

/**
 * Flatten a nested catalog type into the union of its dotted leaf keys.
 * Recursion stops at string leaves and at `{ one, other }` plural leaves.
 */
export type FlattenKeys<T, P extends string = ""> = {
  [K in keyof T & string]: T[K] extends string
    ? `${P}${K}`
    : T[K] extends PluralForm
      ? `${P}${K}`
      : T[K] extends object
        ? FlattenKeys<T[K], `${P}${K}.`>
        : never;
}[keyof T & string];

// ---- locale seam -----------------------------------------------------------
// English-only today. `setLocale` is the single switch a future language wires
// into; surfaces read `getLocale()` to pick their catalog.
let activeLocale = "en";
export function setLocale(locale: string): void {
  activeLocale = locale;
}
export function getLocale(): string {
  return activeLocale;
}

// ---- primitives ------------------------------------------------------------
export function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_match, token: string) =>
    Object.prototype.hasOwnProperty.call(params, token)
      ? String(params[token])
      : `{${token}}`,
  );
}

function isPluralForm(value: unknown): value is PluralForm {
  return (
    typeof value === "object" &&
    value !== null &&
    "other" in (value as Record<string, unknown>)
  );
}

/**
 * Select a plural form for `count`. English rule only (`count === 1 ? one : other`);
 * swap in `Intl.PluralRules(locale)` here when a non-English locale needs it.
 */
export function plural(
  form: PluralForm,
  count: number,
  _locale: string = activeLocale,
): string {
  return count === 1 ? form.one : form.other;
}

// ---- factory ---------------------------------------------------------------
/**
 * Build a `t()` bound to one surface's catalog. `getCatalog` is called per
 * invocation so a `setLocale()` switch takes effect without rebuilding `t`.
 * Pin the key type to the `en` shape at the call site: `createT<typeof en>(...)`.
 */
export function createT<C>(getCatalog: () => C) {
  type Key = FlattenKeys<C>;
  return function t(key: Key, params?: Params): string {
    const parts = (key as string).split(".");
    let node: unknown = getCatalog();
    for (const part of parts) {
      if (
        node != null &&
        typeof node === "object" &&
        part in (node as Record<string, unknown>)
      ) {
        node = (node as Record<string, unknown>)[part];
      } else {
        return key as string; // missing key → surface the key itself (dev signal)
      }
    }
    if (typeof node === "string") return interpolate(node, params);
    if (isPluralForm(node)) {
      const count = Number(params?.count ?? 0);
      return interpolate(plural(node, count), { ...params, count });
    }
    return key as string; // key pointed at a subtree, not a leaf
  };
}
